import {
	ChangeDetectorRef,
	Component, forwardRef, Inject, ViewEncapsulation
}                                from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient }            from '@angular/common/http';
import { DashboardPanelComponentBase } from '@cs/components/shared';
import {
	ArrayUtils,
	DataDescribed,
	PropertyAnnotation,
	updateTargetSources
}                                from '@cs/core';
import { hasPropertyOf }         from '@cs/core/utils';
import { FormatProviderService } from '@cs/common';
import { FilterCompareBarQuery } from '@cs/components/filter-and-compare-bar';
import { DashboardEventHub }     from '../../dashboard-event-hub.service';
import { DashboardPanelOptions } from '../../models/dashboard-panel-options';
import { DashboardComponent }          from '../../dashboard.component';
import { DashboardPanelSettingEventArgs } from '../../models/dashboard-panel-setting-event-args';
import {
	CardAction,
	CardFieldValue,
	CardLayoutAnnotation,
	CardViewModel
}                                from './dashboard-cards.models';

@Component({
	selector:      'cs-dashboard-cards',
	templateUrl:   './dashboard-cards.component.html',
	encapsulation: ViewEncapsulation.None,
	standalone:    false
})
export class DashboardCardsComponent extends DashboardPanelComponentBase<any> {

	name: string;
	cards: CardViewModel[] = [];
	actions: CardAction[] = [];
	cardLayout: CardLayoutAnnotation = {};
	isEmpty = true;
	selectedCardId: any = null;

	private _detailOptionSubscribed = false;

	constructor(
		@Inject(forwardRef(() => DashboardEventHub)) private dashboardEventHub: DashboardEventHub,
		@Inject(DashboardPanelOptions) private dashboardOptions: DashboardPanelOptions,
		private changeRef: ChangeDetectorRef,
		private formatService: FormatProviderService,
		private http: HttpClient,
		private filterCompareBarQuery: FilterCompareBarQuery,
		private route: ActivatedRoute,
		private router: Router
	) {
		super();
		this.name = dashboardOptions.name;
		// Restore selection from URL so it survives data refreshes / navbar churn.
		// Coerce numeric strings back to numbers so strict-equality template checks match the row key.
		const urlParam = this.route.snapshot.queryParams[this._selectionQueryKey()];
		if (urlParam != null && urlParam !== '') {
			this.selectedCardId = /^-?\d+$/.test(String(urlParam)) ? Number(urlParam) : urlParam;
		}
	}

	private _selectionQueryKey(): string {
		return `selectedCard.${this.name}`;
	}

	/** True when the given card matches the restored/current selection (loose-equality to bridge string/number). */
	isSelected(card: CardViewModel): boolean {
		if (this.selectedCardId == null) return false;
		const keyField = (this._data as DataDescribed<any>)?.dataAnnotation?.fields?.find(f => f.key);
		if (!keyField) return false;
		// tslint:disable-next-line:triple-equals
		return card.row[keyField.id as string] == this.selectedCardId;
	}

	dataChanged(value: any): void {
		this.parseCards(value);
	}

	update(data: any): void {
		if (this._data && ArrayUtils.isEqual(this._data, data)) return;
		this.data = data;
	}

	onActionClicked(card: CardViewModel, action: CardAction, $event: MouseEvent): void {
		$event.stopPropagation();

		const result = updateTargetSources(
			{ row: card.row, column: action as unknown as PropertyAnnotation<any> },
			this._data,
			this.name
		);

		if (result === null) return;

		result.triggerId = action.name;
		this.dashboardEventHub.isDashboardEntryIsClicked.next(result);
	}

	onCardClicked(card: CardViewModel): void {
		// Always select the card for detail panel
		this.selectCard(card);

		// If card is also clickable for IPA navigation
		if (this.cardLayout.isCardClickable) {
			const result = updateTargetSources(
				{ row: card.row, column: null },
				this._data,
				this.name
			);

			if (result === null) return;

			this.dashboardEventHub.isDashboardEntryIsClicked.next(result);
		}
	}

	toggleExpand(card: CardViewModel, $event: MouseEvent): void {
		$event.stopPropagation();
		card.isExpanded = !card.isExpanded;
	}

	getActionIcon(icon: string): string {
		if (!icon) return 'mdi mdi-dots-vertical';

		switch (icon.toUpperCase()) {
			case 'EDIT':   return 'mdi mdi-pencil-outline';
			case 'DELETE': return 'mdi mdi-delete-outline';
			case 'COPY':   return 'mdi mdi-content-copy';
			default:       return 'mdi mdi-' + icon.toLowerCase();
		}
	}

	private selectCard(card: CardViewModel): void {
		const keyField = (this._data as DataDescribed<any>)?.dataAnnotation?.fields?.find(f => f.key);
		this.selectedCardId = keyField ? card.row[keyField.id as string] : null;
		// Persist selection in URL so it survives data refresh / unrelated navbar churn
		this.router.navigate([], {
			queryParams:         { [this._selectionQueryKey()]: this.selectedCardId },
			queryParamsHandling: 'merge'
		});
		this.changeRef.detectChanges();

		if (this.cardLayout.detailEndpoint && this.cardLayout.detailPanelName) {
			// Preserve current panel option selections (e.g. Department filter) across card switches
			const dashboard    = this.dashboardEventHub.getParent() as DashboardComponent;
			const existingPanel = dashboard?.dashboardGrid?.getPanel(this.cardLayout.detailPanelName);
			const currentPanelSettings = existingPanel?.options?.data
				? { [this.cardLayout.detailPanelName]: { ...(existingPanel.options.data as object) } }
				: {};
			this.loadDetailPanel(card, currentPanelSettings);
		}
	}

	private loadDetailPanel(card: CardViewModel | null, panelSettings: Record<string, any> = {}): void {
		const endpoint  = this.cardLayout.detailEndpoint;
		const panelName = this.cardLayout.detailPanelName;

		// Build selection payload — use stored selectedCardId when reloading after option change
		const navParams = this.filterCompareBarQuery.getValue().mainbarResultParams || {};
		const keyField  = (this._data as DataDescribed<any>)?.dataAnnotation?.fields?.find(f => f.key);
		const id        = card != null
			? (keyField ? card.row[keyField.id as string] : '')
			: this.selectedCardId;

		if (id == null) return;

		// Subscribe once to forward SetOptions changes back through the detailEndpoint
		if (!this._detailOptionSubscribed) {
			this._detailOptionSubscribed = true;
			const dashboard = this.dashboardEventHub.getParent() as DashboardComponent;
			if (dashboard) {
				// Tell DashboardBase not to handle option changes for this detail panel —
				// loadDetailPanel (below) is the sole handler.
				dashboard.managedDetailPanels.add(this.cardLayout.detailPanelName);
				dashboard.panelOptionSelected.subscribe((args: DashboardPanelSettingEventArgs) => {
					if (args.panelName === this.cardLayout.detailPanelName && this.selectedCardId != null) {
						this.loadDetailPanel(null, args.panelSettings as Record<string, any>);
					}
				});
			}
		}

		// Send Id and navParams at top level so _Params.Id is set on the server.
		// panelSettings is also top-level so _Params.panelSettings is available.
		const body: Record<string, any> = { ...navParams, Id: id };
		if (panelSettings && Object.keys(panelSettings).length > 0) {
			body['panelSettings'] = panelSettings;
		}

		this.dashboardEventHub.panelIsLoading(true, panelName);

		this.http.post<any>(endpoint, body).subscribe({
			next: (response) => {
				if (response.panels && response.panels.length > 0) {
					const serverPanel = response.panels[0];

					// Get the parent dashboard and patch the panel in the rendered grid
					const dashboard = this.dashboardEventHub.getParent() as DashboardComponent;
					if (dashboard && dashboard.dashboardGrid) {
						const existingPanel = dashboard.dashboardGrid.getPanel(panelName);
						if (existingPanel) {
							// Update the panel properties to match the server response
							existingPanel.type      = serverPanel.type;
							existingPanel.data      = serverPanel.data;
							existingPanel.label     = serverPanel.label || existingPanel.label;
							existingPanel.note      = serverPanel.note || null;
							existingPanel.meta      = serverPanel.meta || null;
							existingPanel.options   = serverPanel.options != null ? new DataDescribed(serverPanel.options) : null;
							existingPanel.component = (dashboard as any).getComponentType(existingPanel);

							// Re-register the component by triggering change detection
							dashboard.detectChanges();
						}
					}
				}
				this.dashboardEventHub.panelIsLoading(false, panelName);
			},
			error: () => {
				this.dashboardEventHub.panelIsLoading(false, panelName);
			}
		});
	}

	private parseCards(value: any): void {
		if (!value) {
			this.cards   = [];
			this.actions = [];
			this.isEmpty = true;
			return;
		}

		const described = value instanceof DataDescribed
			? value
			: new DataDescribed(value);

		this._data = described;

		// Read layout
		const layout = described.layout as any;
		if (hasPropertyOf(layout, 'cards')) {
			this.cardLayout = layout.cards as CardLayoutAnnotation;
			this.actions    = this.cardLayout.actions || [];
		} else {
			this.cardLayout = {};
			this.actions    = [];
		}

		// Group fields by role
		const fields: PropertyAnnotation<any>[] = described.dataAnnotation?.fields || [];
		const titleFields    = fields.filter(f => f.groupId === 'title' && f.visible !== false);
		const subtitleFields = fields.filter(f => f.groupId === 'subtitle' && f.visible !== false);
		const tagFields      = fields.filter(f => f.groupId === 'tag' && f.visible !== false);
		const detailFields   = fields.filter(f => f.groupId === 'detail' && f.visible !== false);

		// Build card view models from data rows
		const rows = Array.isArray(described.data) ? described.data : [];
		this.isEmpty = rows.length === 0;

		this.cards = rows.map(row => {
			const titleField = titleFields.length > 0
				? this.buildFieldValue(titleFields[0], row)
				: null;

			const subtitleField = subtitleFields.length > 0
				? this.buildFieldValue(subtitleFields[0], row)
				: null;

			const tags = tagFields.map(f => this.buildFieldValue(f, row));

			const details = detailFields.map(f => this.buildFieldValue(f, row));

			return {
				row,
				titleField,
				subtitleField,
				tagFields:    tags,
				detailFields: details,
				isExpanded:   this.cardLayout.defaultExpanded || false
			} as CardViewModel;
		});

		this.changeRef.detectChanges();

		// After cards are built, if a selection was restored from the URL, find the matching card
		// and re-trigger the detail load so the split-view shows the right overrides.
		if (this.selectedCardId != null && this.cardLayout.detailEndpoint && this.cardLayout.detailPanelName) {
			const restored = this.cards.find(c => this.isSelected(c));
			if (restored && !this._detailRestored) {
				this._detailRestored = true;
				this.loadDetailPanel(restored, {});
			}
		}
	}

	/** Guard so the restore only fires once per component lifetime (not on every data refresh). */
	private _detailRestored = false;

	private buildFieldValue(field: PropertyAnnotation<any>, row: any): CardFieldValue {
		let value = row[field.id as string];

		// Resolve lookups
		if (field.lookup && this._data) {
			const resolved = (this._data as DataDescribed<any>).resolveValueWithLookup(value, field.lookup);
			if (resolved != null) value = resolved;
		}

		// Format
		value = this.formatService.formatByPropertyAnnotation(field, value);

		return {
			annotation: field,
			value,
			icon: field.description || null
		};
	}
}
